import { cleanConditionSyntax } from './templateParser.js';
import { Document, Packer, Paragraph, TextRun, AlignmentType, PageBreak } from 'docx';
import { toAlpha, toRoman, getOrderedListMarker } from './docxListHelpers.js';
import { getSimilarity, getNodeTableFingerprint, createTableDuplicateChecker } from './docxTableHelpers.js';
import { decodeHtmlEntities } from './docxTextHelpers.js';
export async function exportToDocx(editorJsonRaw, variables = [], userPageSettings = {}) {
    const pageSettings = {
        fontSize: 11,
        topMargin: 1,
        bottomMargin: 1,
        leftMargin: 1,
        rightMargin: 1,
        headerDistance: 0.5,
        footerDistance: 0.5,
        ...(userPageSettings || {})
    };
    const editorJson = JSON.parse(JSON.stringify(editorJsonRaw));
    const rawNodes = [...(editorJson.content || [])];

    // Helper: detect if a node is an empty / trailing blank line or trailing page break
    function isTrailingBlankNode(node) {
        if (!node) return true;
        if (node.type === 'pageBreak' || node.type === 'lineBreakBlock' || node.type === 'horizontalLineBlock' || node.type === 'horizontalRule') {
            return false;
        }
        if (node.type === 'paragraph') {
            let text = '';
            const extract = (n) => {
                if (n.text) text += n.text;
                if (n.type === 'variableTag' || n.attrs?.variableKey || n.attrs?.label) {
                    text += ' ' + (n.attrs?.label || n.attrs?.variableKey || '');
                }
                if (n.content) n.content.forEach(extract);
            };
            extract(node);
            const clean = text.replace(/[\s\u00a0\u200B\r\n]+/g, '').trim();
            const hasSpecial = Boolean(node.content?.some(c => c.type === 'image' || c.type === 'whiteBlankImage' || c.type === 'horizontalLineBlock' || c.type === 'horizontalRule'));
            return !clean && !hasSpecial;
        }
        return false;
    }

    // Trim trailing blank lines / page breaks from the end of the document
    while (rawNodes.length > 0 && isTrailingBlankNode(rawNodes[rawNodes.length - 1])) {
        rawNodes.pop();
    }

    // Clean redundant empty paragraphs before/after page breaks and collapse multiple consecutive empty paragraphs
    const contentNodes = [];
    for (let i = 0; i < rawNodes.length; i++) {
        const cur = rawNodes[i];
        const isCurBlank = isTrailingBlankNode(cur) && cur.type !== 'pageBreak' && cur.type !== 'lineBreakBlock';
        const nextNode = rawNodes[i + 1];
        const prevNode = contentNodes[contentNodes.length - 1];

        // Skip empty paragraph directly adjacent to a page break
        if (isCurBlank && ((nextNode && nextNode.type === 'pageBreak') || (prevNode && prevNode.type === 'pageBreak'))) {
            continue;
        }
        // Collapse consecutive empty paragraphs to at most 1
        if (isCurBlank && prevNode && isTrailingBlankNode(prevNode) && prevNode.type !== 'pageBreak' && prevNode.type !== 'lineBreakBlock') {
            continue;
        }
        contentNodes.push(cur);
    }

    const docxChildren = [];
    function getVariableLabel(node) {
        const rawKey = node.attrs?.variableKey;
        const rawLabel = node.attrs?.label;
        const cleanKey = decodeHtmlEntities(rawKey);
        const cleanLabel = decodeHtmlEntities(rawLabel);

        const v = variables.find(item => 
            item.key === rawKey || 
            item.key === cleanKey || 
            item.label === rawLabel || 
            item.label === cleanLabel ||
            decodeHtmlEntities(item.key) === cleanKey ||
            decodeHtmlEntities(item.label) === cleanLabel
        );
        const result = v ? v.label : (cleanLabel || cleanKey || 'Unknown');
        return decodeHtmlEntities(result);
    }
    function buildConditionString(attrs) {
        if (!attrs)
            return '';
        const rulesList = attrs.rules || attrs.conditions || [];
        if (Array.isArray(rulesList) && rulesList.length > 0) {
            const parts = rulesList.map((r) => {
                const fieldKey = decodeHtmlEntities(r.field || r.variable || '').replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
                const vObj = variables.find(item => item.key === fieldKey || item.label === fieldKey || decodeHtmlEntities(item.key) === fieldKey || decodeHtmlEntities(item.label) === fieldKey);
                const vLabel = decodeHtmlEntities(vObj ? vObj.label : fieldKey).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
                const op = decodeHtmlEntities(r.operator || 'exists').replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').trim();
                const val = decodeHtmlEntities(r.value !== undefined && r.value !== null ? String(r.value) : '').replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
                if (op === 'exists' || op === 'is_not_empty') {
                    return `<<${vLabel}>>`;
                }
                else if (op === 'not_exists' || op === 'is_empty') {
                    return `<<${vLabel}>> == ''`;
                }
                else if (op === 'equals' || op === '==') {
                    return `<<${vLabel}>> == ${val}`;
                }
                else if (op === 'not_equals' || op === '!=') {
                    return `<<${vLabel}>> != ${val}`;
                }
                else if (op === 'greater_than' || op === '>') {
                    return `<<${vLabel}>> > ${val}`;
                }
                else if (op === 'less_than' || op === '<') {
                    return `<<${vLabel}>> < ${val}`;
                }
                else if (op === 'greater_than_or_equal' || op === '>=') {
                    return `<<${vLabel}>> >= ${val}`;
                }
                else if (op === 'less_than_or_equal' || op === '<=') {
                    return `<<${vLabel}>> <= ${val}`;
                }
                else if (op === 'contains') {
                    return `<<${vLabel}>> contains ${val}`;
                }
                else if (op === 'does_not_contain') {
                    return `<<${vLabel}>> not contains ${val}`;
                }
                else if (op === 'OR' || op === '||') {
                    return `<<${vLabel}>>`;
                }
                return val ? `<<${vLabel}>> ${op} ${val}` : `<<${vLabel}>>`;
            });
            const joiner = (attrs.operator === 'OR' || attrs.operator === '||') ? ' || ' : ' && ';
            return `[${parts.join(joiner)}]`;
        }
        else if (attrs.label) {
            let l = cleanConditionSyntax(attrs.label, attrs);
            l = l.replace(/^\[\s*/, '').replace(/\s*\]$/, '').replace(/\s+EXISTS\b/gi, '').trim();
            if (!l) return '';
            return `[${l}]`;
        }
        return '';
    }

    // Merge any stray {= [cond]} paragraph preceding a whiteBlankImage node
    for (let i = 0; i < contentNodes.length; i++) {
        const cur = contentNodes[i];
        if (cur && cur.type === 'paragraph') {
            let text = '';
            let condMark = null;
            if (cur.content) {
                for (const c of cur.content) {
                    if (c.text) text += c.text;
                    const cm = c.marks?.find(m => m.type === 'conditionalBlock');
                    if (cm && !condMark) condMark = cm;
                }
            }
            const cleanT = text.replace(/[\s\u00a0\r\n]+/g, '').trim();
            if (condMark && (cleanT === '=' || cleanT === '==' || cleanT === '=>' || cleanT === '')) {
                let targetSig = null;
                for (let j = i + 1; j < contentNodes.length; j++) {
                    const candidate = contentNodes[j];
                    if (candidate.type === 'whiteBlankImage') {
                        targetSig = candidate;
                        break;
                    } else if (candidate.type === 'paragraph' && candidate.content?.length === 1 && candidate.content[0].type === 'whiteBlankImage') {
                        targetSig = candidate.content[0];
                        break;
                    } else if (isTrailingBlankNode(candidate)) {
                        continue;
                    } else {
                        break;
                    }
                }
                if (targetSig) {
                    if (!targetSig.attrs) targetSig.attrs = {};
                    if (!targetSig.attrs.condition) {
                        const rawCond = buildConditionString(condMark.attrs) || condMark.attrs?.condition || '';
                        targetSig.attrs.condition = rawCond;
                        targetSig.attrs.conditionData = condMark.attrs?.rules || condMark.attrs?.conditions ? JSON.stringify(condMark.attrs) : '';
                    }
                    if (!targetSig.attrs.align || targetSig.attrs.align === 'left' || targetSig.attrs.align === 'center') {
                        targetSig.attrs.align = 'right';
                    }
                    contentNodes.splice(i, 1);
                    i--;
                }
            }
        }
    }
    function closeMark(itemList, startIdx, endIdx, marker) {
        if (startIdx < 0 || endIdx < 0 || startIdx >= itemList.length || endIdx >= itemList.length) return;
        
        if (!itemList[startIdx].text.startsWith(marker)) {
            const m = itemList[startIdx].text.match(/^(\s*)([\s\S]*)$/);
            if (m) itemList[startIdx].text = m[1] + marker + m[2];
            else itemList[startIdx].text = marker + itemList[startIdx].text;
        }

        if (!itemList[endIdx].text.endsWith(marker)) {
            const m = itemList[endIdx].text.match(/^([\s\S]*?)(\s*)$/);
            if (m) itemList[endIdx].text = m[1] + marker + m[2];
            else itemList[endIdx].text = itemList[endIdx].text + marker;
        }
    }

    function groupMark(itemList, isMarkProp, marker) {
        if (!itemList || itemList.length === 0) return itemList;

        const isWhitespace = (it) => !it.text || it.text.trim().length === 0;

        let inMark = false;
        let markStartIdx = -1;
        let lastMarkedContentIdx = -1;

        for (let i = 0; i < itemList.length; i++) {
            const it = itemList[i];
            const hasMark = Boolean(it[isMarkProp]);
            const isWs = isWhitespace(it);

            if (!inMark) {
                if (hasMark && !isWs) {
                    inMark = true;
                    markStartIdx = i;
                    lastMarkedContentIdx = i;
                }
            } else {
                if (hasMark && !isWs) {
                    lastMarkedContentIdx = i;
                } else if (isWs) {
                    let nextHasMark = false;
                    for (let j = i + 1; j < itemList.length; j++) {
                        if (!isWhitespace(itemList[j])) {
                            nextHasMark = Boolean(itemList[j][isMarkProp]);
                            break;
                        }
                    }
                    if (!nextHasMark) {
                        closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
                        inMark = false;
                        markStartIdx = -1;
                        lastMarkedContentIdx = -1;
                    }
                } else if (!hasMark) {
                    closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
                    inMark = false;
                    markStartIdx = -1;
                    lastMarkedContentIdx = -1;
                }
            }
        }

        if (inMark && markStartIdx !== -1 && lastMarkedContentIdx !== -1) {
            closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
        }

        return itemList;
    }

    const applyBoldGrouping = (...args) => applyMarkupGrouping(...args);
    function applyMarkupGrouping(items, defaultBold = false, defaultUnderline = false) {
        if (!items || items.length === 0) return items;
        const cloned = items.map(it => ({ ...it }));
        let res = groupMark(cloned, 'isBold', '**');
        res = groupMark(res, 'isUnderline', '__');
        res = groupMark(res, 'isClearSpace', '@');
        res = groupMark(res, 'isDollar', '$');
        res.forEach(item => {
            if (item.text) {
                item.text = item.text
                    .replace(/<<\*\*([\s\S]*?)\*\*>>/g, '**<<$1>>**')
                    .replace(/\*\*<<\*\*([\s\S]*?)\*\*>>\*\*/g, '**<<$1>>**');
            }
        });
        return res;
    }

    function processContentGrouped(contentList, defaultBold = false, defaultUnderline = false, customFontSize, isHeading = false) {
        const size = customFontSize || pageSettings.fontSize * 2;
        const rawItems = [];
        let i = 0;
        while (i < contentList.length) {
            const child = contentList[i];
            const marks = child.marks || [];
            const condMark = marks.find((m) => m.type === 'conditionalBlock');
            const optMark = marks.find((m) => m.type === 'optionalBlock');
            const clearMark = marks.find((m) => m.type === 'clearSpaceBlock');
            const dollarMark = marks.find((m) => m.type === 'dollarBlock');
            if (condMark) {
                const condRuleId = condMark.attrs?.ruleId || JSON.stringify(condMark.attrs?.rules || condMark.attrs?.conditions || '');
                const group = [];
                while (i < contentList.length) {
                    const curChild = contentList[i];
                    const curCond = curChild.marks?.find((m) => m.type === 'conditionalBlock');
                    const curRuleId = curCond?.attrs?.ruleId || JSON.stringify(curCond?.attrs?.rules || curCond?.attrs?.conditions || '');
                    if (curCond && curRuleId === condRuleId) {
                        group.push(curChild);
                        i++;
                    }
                    else {
                        break;
                    }
                }
                const groupItems = [];
                group.forEach((n) => {
                    let t = '';
                    if (n.type === 'text') {
                        t = (n.text || '').replace(/^;\s*/, '');
                        if (t.startsWith('== ') || t.startsWith('=> ') || t.startsWith('<= ')) {
                            t = t.substring(3);
                        }
                        else if (t.startsWith('### ') || t.startsWith('## ') || t.startsWith('# ')) {
                            t = t.replace(/^#{1,3}\s*/, '');
                        }
                    }
                    else if (n.type === 'variableTag') {
                        const varLabel = getVariableLabel(n); t = `<<${varLabel}>>`;
                    }
                    else if (n.type === 'pageBreak') {
                        t = '<page break>';
                    }
                    else if (n.type === 'horizontalLineBlock' || n.type === 'horizontalRule') {
                        t = '<line>';
                    }
                    const isBold = !isHeading && (defaultBold || (n.marks?.some((m) => m.type === 'bold') ?? false));
                    const isUnderline = !isHeading && (defaultUnderline || (n.marks?.some((m) => m.type === 'underline') ?? false));
                    const isItalic = n.marks?.some((m) => m.type === 'italic') ?? false;
                    const isClearSpace = n.marks?.some((m) => m.type === 'clearSpaceBlock') ?? false;
                    const isDollar = n.marks?.some((m) => m.type === 'dollarBlock') ?? false;
                    if (t)
                        groupItems.push({ text: t, isBold, isUnderline, isItalic, isClearSpace, isDollar, size });
                });
                const condStr = buildConditionString(condMark.attrs);
                if (groupItems.length > 0) {
                    groupItems[0].text = '{' + groupItems[0].text;
                    const lastIdx = groupItems.length - 1;
                    if (condStr) {
                        const lastText = groupItems[lastIdx].text;
                        const separator = lastText.endsWith(' ') ? '' : ' ';
                        groupItems[lastIdx].text = `${lastText}${separator}${condStr}}`;
                    }
                    else {
                        groupItems[lastIdx].text = `${groupItems[lastIdx].text}}`;
                    }
                    rawItems.push(...groupItems);
                }
            }
            else if (optMark) {
                const group = [];
                while (i < contentList.length) {
                    const curChild = contentList[i];
                    const curOpt = curChild.marks?.find((m) => m.type === 'optionalBlock');
                    if (curOpt) {
                        group.push(curChild);
                        i++;
                    }
                    else {
                        break;
                    }
                }
                const groupItems = [];
                group.forEach((n) => {
                    let t = '';
                    if (n.type === 'text') {
                        t = n.text || '';
                        if (t.startsWith('== ') || t.startsWith('=> ') || t.startsWith('<= ')) {
                            t = t.substring(3);
                        }
                        else if (t.startsWith('### ') || t.startsWith('## ') || t.startsWith('# ')) {
                            t = t.replace(/^#{1,3}\s*/, '');
                        }
                    }
                    else if (n.type === 'variableTag') {
                        const varLabel = getVariableLabel(n); t = `<<${varLabel}>>`;
                    }
                    else if (n.type === 'pageBreak') {
                        t = '<page break>';
                    }
                    else if (n.type === 'horizontalLineBlock' || n.type === 'horizontalRule') {
                        t = '<line>';
                    }
                    const isBold = !isHeading && (defaultBold || (n.marks?.some((m) => m.type === 'bold') ?? false));
                    const isUnderline = !isHeading && (defaultUnderline || (n.marks?.some((m) => m.type === 'underline') ?? false));
                    const isItalic = n.marks?.some((m) => m.type === 'italic') ?? false;
                    const isClearSpace = n.marks?.some((m) => m.type === 'clearSpaceBlock') ?? false;
                    const isDollar = n.marks?.some((m) => m.type === 'dollarBlock') ?? false;
                    if (t)
                        groupItems.push({ text: t, isBold, isUnderline, isItalic, isClearSpace, isDollar, size });
                });
                if (groupItems.length > 0) {
                    groupItems[0].text = '{' + groupItems[0].text;
                    groupItems[groupItems.length - 1].text = groupItems[groupItems.length - 1].text + '}';
                    rawItems.push(...groupItems);
                }
            }
                        
            else if (child.type === 'hardBreak' || child.type === 'br') {
                rawItems.push({ text: '', break: 1, isBold: false, isUnderline: false, isItalic: false, size });
                i++;
            }
            else {
                let t = '';
                if (child.type === 'text') {
                    t = child.text || '';
                    if (t.startsWith('== ') || t.startsWith('=> ') || t.startsWith('<= ')) {
                        t = t.substring(3);
                    }
                    else if (t.startsWith('### ') || t.startsWith('## ') || t.startsWith('# ')) {
                        t = t.replace(/^#{1,3}\s*/, '');
                    }
                }
                else if (child.type === 'variableTag') {
                    const varLabel = getVariableLabel(child); t = `<<${varLabel}>>`;
                }
                else if (child.type === 'pageBreak') {
                    t = '<page break>';
                }
                else if (child.type === 'horizontalLineBlock' || child.type === 'horizontalRule') {
                    t = '<line>';
                }
                const isBold = !isHeading && (defaultBold || (child.marks?.some((m) => m.type === 'bold') ?? false));
                const isUnderline = !isHeading && (defaultUnderline || (child.marks?.some((m) => m.type === 'underline') ?? false));
                const isItalic = child.marks?.some((m) => m.type === 'italic') ?? false;
                const isClearSpace = child.marks?.some((m) => m.type === 'clearSpaceBlock') ?? false;
                const isDollar = child.marks?.some((m) => m.type === 'dollarBlock') ?? false;
                if (t)
                    rawItems.push({ text: t, isBold, isUnderline, isItalic, isClearSpace, isDollar, size });
                i++;
            }
        }
        return rawItems;
    }
    function processNode(node, childrenArray, listContext) {
        if (node.type === 'pageBreak') {
            childrenArray.push(new Paragraph({
                children: [
                    new TextRun({
                        text: '<page break>',
                        font: "Times New Roman",
                        size: pageSettings.fontSize * 2
                    })
                ],
                spacing: {
                    line: 360,
                    lineRule: "auto",
                    after: 180,
                    before: 0
                }
            }));
            return;
        }
        if (node.type === 'horizontalRule' || node.type === 'horizontalLineBlock') {
            childrenArray.push(new Paragraph({
                children: [
                    new TextRun({
                        text: '<line>',
                        font: "Times New Roman",
                        size: pageSettings.fontSize * 2
                    })
                ],
                spacing: {
                    line: 360,
                    lineRule: "auto",
                    after: 180,
                    before: 0
                }
            }));
            return;
        }
        if (node.type === 'whiteBlankImage') {
            let w = node.attrs?.width || '1.0';
            let h = node.attrs?.height || '1.0';
            let filename = (node.attrs?.filename || 'signature.png').trim();
            if (!filename) filename = 'signature.png';
            if (/\s/.test(filename)) filename = filename.replace(/\s+/g, '_');
            if (!/\.png$/i.test(filename)) filename = filename + '.png';

            const numW = parseFloat(w);
            const numH = parseFloat(h);
            if (isNaN(numW) || numW <= 0) w = '1.0';
            if (isNaN(numH) || numH <= 0) h = '1.0';

            const align = node.attrs?.align || 'left';
            let sigAlign = AlignmentType.LEFT;
            if (align === 'center') sigAlign = AlignmentType.CENTER;
            else if (align === 'right') sigAlign = AlignmentType.RIGHT;

            let conditionStr = cleanConditionSyntax(node.attrs?.condition, node.attrs?.conditionData);
            if (conditionStr) {
                conditionStr = conditionStr.replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();
            }

            let exportText = `! ${w} ${h} ${filename}`;
            if (conditionStr) {
                let alignSyntax = '';
                if (align === 'right') alignSyntax = '=> ';
                else if (align === 'center') alignSyntax = '== ';
                exportText = `{${alignSyntax ? alignSyntax : ' '}! ${w} ${h} ${filename} [${conditionStr}] }`;
            }

            childrenArray.push(new Paragraph({
                alignment: sigAlign,
                children: [
                    new TextRun({
                        text: exportText,
                        font: "Times New Roman",
                        size: pageSettings.fontSize * 2
                    })
                ],
                spacing: {
                    line: 360,
                    lineRule: "auto",
                    after: 180,
                    before: 0
                }
            }));
            return;
        }
        if (node.type === 'lineBreakBlock') {
            childrenArray.push(new Paragraph({
                children: [
                    new TextRun({
                        text: '<line break>',
                        font: "Times New Roman",
                        size: pageSettings.fontSize * 2
                    })
                ],
                spacing: {
                    line: 360,
                    lineRule: "auto",
                    after: 180,
                    before: 0
                }
            }));
            return;
        }
        if (node.type === 'heading') {
            const level = node.attrs?.level || 1;
            const textAlign = node.attrs?.textAlign || 'center';
            const alignment = textAlign === 'right' ? AlignmentType.RIGHT
                : textAlign === 'left' ? AlignmentType.LEFT
                    : AlignmentType.CENTER;
            let prefix = '';
            if (level === 3)
                prefix = '### ';
            else if (level === 2)
                prefix = '## ';
            else if (level === 1)
                prefix = '# ';
            let isBold = level === 2 || level === 3;
            let isUnderline = level === 3;
            let fontSize = pageSettings.fontSize * 2;
            const rawItems = processContentGrouped(node.content || [], false, false, fontSize, true);
            let headingText = rawItems.map(it => it.text).join('').trim();
            if (level === 2 && (!headingText || headingText === '##')) {
                childrenArray.push(new Paragraph({
                    children: [new TextRun({ text: "##", font: "Times New Roman", size: fontSize })],
                    alignment: alignment,
                    spacing: {
                        line: 360,
                        lineRule: "auto",
                        after: 200,
                        before: 100
                    }
                }));
                return;
            }
            if (rawItems.length > 0 && prefix) {
                const first = rawItems[0].text.trimStart();
                if (/^(?:###|##|#|==)/.test(first)) {
                    rawItems[0].text = rawItems[0].text.replace(/^(?:###|##|#|==)\s*/, '');
                }
                rawItems[0].text = prefix + rawItems[0].text;
            }
            const runs = [];
            rawItems.forEach((item) => {
                const runOptions = {
                    text: item.text,
                    font: "Times New Roman",
                    size: item.size,
                    ...(item.break ? { break: item.break } : {})
                };
                runs.push(new TextRun(runOptions));
            });
            if (runs.length === 0) {
                runs.push(new TextRun({ text: prefix.trim(), font: "Times New Roman", size: fontSize }));
            }
            childrenArray.push(new Paragraph({
                children: runs,
                alignment: alignment,
                spacing: {
                    line: 360,
                    lineRule: "auto",
                    after: 200,
                    before: 100
                }
            }));
            return;
        }
        if (node.type === 'paragraph') {
            let nodeText = '';
            const extractText = (n) => {
                if (n.text) nodeText += n.text;
                if (n.type === 'variableTag' || n.attrs?.variableKey || n.attrs?.label) {
                    nodeText += ' ' + (n.attrs?.label || n.attrs?.variableKey || 'var');
                }
                if (n.content) n.content.forEach(extractText);
            };
            extractText(node);
            const cleanText = nodeText.replace(/\u00a0/g, ' ').trim();
            if (/^(?:e-?stamp\s+)?certificate\s+(?:no|number)[\s.:]*[A-Z0-9\-_/\s]+$/i.test(cleanText) || /^certificate\s+no\s*[:.]\s*in-[a-z0-9]+/i.test(cleanText)) {
                return;
            }
            if (cleanText === '##') {
                childrenArray.push(new Paragraph({
                    children: [new TextRun({ text: "##", font: "Times New Roman", size: pageSettings.fontSize * 2 })],
                    spacing: {
                        line: 360,
                        lineRule: "auto",
                        after: 180,
                        before: 0
                    }
                }));
                return;
            }
            if (!cleanText || cleanText === '<=' || cleanText === '=>' || cleanText === '==' || cleanText === '#' || cleanText === '###') {
                childrenArray.push(new Paragraph({
                    children: [new TextRun({ text: " ", font: "Times New Roman", size: pageSettings.fontSize * 2 })],
                    spacing: {
                        line: 360,
                        lineRule: "auto",
                        after: 180,
                        before: 0
                    }
                }));
                return;
            }
            const explicitAlign = node.attrs?.textAlign;
            let textAlign = explicitAlign || 'left';
            let explicitSyntaxUsed = false;
            // Check if text content begins with alignment tokens
            const firstChild = node.content && node.content[0];
            if (firstChild && firstChild.type === 'text' && typeof firstChild.text === 'string') {
                const trimmed = firstChild.text.trimStart();
                if (trimmed.startsWith('###')) {
                    textAlign = 'center';
                    explicitSyntaxUsed = true;
                }
                else if (trimmed.startsWith('##')) {
                    textAlign = 'center';
                    explicitSyntaxUsed = true;
                }
                else if (trimmed.startsWith('#')) {
                    textAlign = 'center';
                    explicitSyntaxUsed = true;
                }
                else if (trimmed.startsWith('==')) {
                    textAlign = 'center';
                    explicitSyntaxUsed = true;
                    firstChild.text = trimmed.replace(/^==\s*-?\s*/, '');
                }
                else if (trimmed.startsWith('=>')) {
                    textAlign = 'right';
                    explicitSyntaxUsed = true;
                    firstChild.text = trimmed.replace(/^=>\s*/, '');
                }
                else if (trimmed.startsWith('<=')) {
                    textAlign = 'left';
                    explicitSyntaxUsed = true;
                    firstChild.text = trimmed.replace(/^<=\s*/, '');
                }
            }
            const alignment = textAlign === 'center' ? AlignmentType.CENTER
                : textAlign === 'right' ? AlignmentType.RIGHT
                    : textAlign === 'left' ? AlignmentType.LEFT
                        : AlignmentType.JUSTIFIED;
            // Clean any leading whitespace/hardbreaks at the start of paragraph content
            while (node.content && node.content.length > 0 && (
                (node.content[0].type === 'text' && !(node.content[0].text || '').trim()) ||
                (node.content[0].type === 'hardBreak' || node.content[0].type === 'br')
            )) {
                node.content.shift();
            }
            // Determine if paragraph as a whole is bold and/or underline
            const nonWhitespaceNodes = (node.content || []).filter((c) => {
                if (c.type === 'text')
                    return (c.text || '').trim().length > 0;
                return true;
            });
            const allBold = nonWhitespaceNodes.length > 0 && nonWhitespaceNodes.every((c) => {
                return c.marks?.some((m) => m.type === 'bold');
            });
            const allUnderline = nonWhitespaceNodes.length > 0 && nonWhitespaceNodes.every((c) => {
                return c.marks?.some((m) => m.type === 'underline');
            });
            let prefix = '';
            let suppressBoldMarkup = false;
            let isHeadingLine = false;
            if (textAlign === 'center') {
                if (allBold && allUnderline) {
                    prefix = '### ';
                    suppressBoldMarkup = true;
                    isHeadingLine = true;
                }
                else if (allBold) {
                    prefix = '## ';
                    suppressBoldMarkup = true;
                    isHeadingLine = true;
                }
                else {
                    prefix = '# ';
                    isHeadingLine = true;
                }
            }
            else if (explicitAlign === 'right' || (explicitSyntaxUsed && textAlign === 'right')) {
                prefix = '=> ';
            }
            const rawItems = processContentGrouped(node.content || [], allBold, allUnderline, pageSettings.fontSize * 2, isHeadingLine);
            let formattedItems = rawItems;
            if (!suppressBoldMarkup && !isHeadingLine) {
                formattedItems = applyMarkupGrouping(rawItems, allBold, allUnderline);
            }
            // Add prefix to first text-bearing item if not already present
            if (formattedItems.length > 0 && prefix) {
                const firstTextItem = formattedItems.find(it => it.text !== undefined);
                if (firstTextItem) {
                    const firstText = (firstTextItem.text || '').trimStart();
                    if (!firstText.startsWith(prefix.trim())) {
                        firstTextItem.text = (firstTextItem.text || '').replace(/^(?:###|##|#|==|=>|<=)\s*/, '');
                        firstTextItem.text = prefix + firstTextItem.text;
                    }
                }
            }
            const runs = [];
            formattedItems.forEach((item) => {
                const runOptions = {
                    text: item.text,
                    font: "Times New Roman",
                    size: item.size
                };
                if (item.isBold && !suppressBoldMarkup && !isHeadingLine)
                    runOptions.bold = true;
                // Underline rendered textually as __text__
                if (item.isItalic)
                    runOptions.italics = true;
                runs.push(new TextRun(runOptions));
            });
            if (runs.length === 0) {
                runs.push(new TextRun({ text: " ", font: "Times New Roman", size: pageSettings.fontSize * 2 }));
            }
            let afterTwips = 180;
            if (node.attrs?.marginBottom) {
                const num = parseFloat(node.attrs.marginBottom);
                if (!isNaN(num) && num > 0) {
                    if (String(node.attrs.marginBottom).includes('pt')) {
                        afterTwips = Math.round(num * 20);
                    } else if (String(node.attrs.marginBottom).includes('px')) {
                        afterTwips = Math.round(num * 15);
                    } else {
                        afterTwips = Math.round(num * 20);
                    }
                }
            }
            let beforeTwips = 0;
            if (node.attrs?.marginTop) {
                const num = parseFloat(node.attrs.marginTop);
                if (!isNaN(num) && num > 0) {
                    if (String(node.attrs.marginTop).includes('pt')) {
                        beforeTwips = Math.round(num * 20);
                    } else if (String(node.attrs.marginTop).includes('px')) {
                        beforeTwips = Math.round(num * 15);
                    } else {
                        beforeTwips = Math.round(num * 20);
                    }
                }
            }
            let lineTwips = 360;
            if (node.attrs?.lineHeight) {
                const numLh = parseFloat(node.attrs.lineHeight);
                if (!isNaN(numLh) && numLh > 0) {
                    lineTwips = Math.round(numLh * 240);
                }
            }
            const paragraphOptions = {
                children: runs,
                alignment: alignment,
                spacing: {
                    line: lineTwips,
                    lineRule: "auto",
                    after: afterTwips,
                    before: beforeTwips
                }
            };
            if (node.attrs?.textIndent || node.attrs?.marginLeft) {
                if (!paragraphOptions.indent) paragraphOptions.indent = {};
                if (node.attrs.textIndent) {
                    const numTi = parseFloat(node.attrs.textIndent);
                    if (!isNaN(numTi)) {
                        paragraphOptions.indent.firstLine = Math.round(numTi * 20);
                    }
                }
                if (node.attrs.marginLeft) {
                    const numMl = parseFloat(node.attrs.marginLeft);
                    if (!isNaN(numMl)) {
                        paragraphOptions.indent.left = Math.round(numMl * 20);
                    }
                }
            }
            if (listContext) {
                if (listContext.type === 'bulletList' && listContext.index !== -1) {
                    paragraphOptions.indent = { left: 720 };
                }
                else if (listContext.type === 'orderedList' && listContext.index !== -1) {
                    runs.unshift(new TextRun({ text: getOrderedListMarker(listContext.index, listContext.listStyleType), font: "Times New Roman", size: pageSettings.fontSize * 2 }));
                    paragraphOptions.indent = { left: 720, hanging: 360 };
                }
                else if (listContext.index === -1) {
                    paragraphOptions.indent = { left: 720 };
                }
            }
            childrenArray.push(new Paragraph(paragraphOptions));
            return;
        }
        if (node.type === 'bulletList' || node.type === 'orderedList') {
            (node.content || []).forEach((listItem, index) => {
                if (listItem.type === 'listItem') {
                    (listItem.content || []).forEach((child, childIndex) => {
                        processNode(child, childrenArray, {
                            type: node.type,
                            index: childIndex === 0 ? index : -1,
                            listStyleType: node.attrs?.listStyleType || 'decimal'
                        });
                    });
                } else {
                    processNode(listItem, childrenArray, null);
                }
            });
            return;
        }
        if (node.type === 'table') {
            const tableFp = getNodeTableFingerprint(node);
            // Do not drop tables from the editor document
            const rows = (node.content || []).filter((r) => r.type === 'tableRow');
            if (rows.length === 0)
                return;
            const colCount = Math.max(...rows.map((r) => (r.content || []).length), 1);
            function extractCellFormattedText(cellNode) {
                if (!cellNode)
                    return ' ';
                const allContent = [];
                (cellNode.content || []).forEach((c) => {
                    if (c.content) {
                        c.content.forEach(n => {
                            if (n.type !== 'hardBreak' && n.type !== 'br') allContent.push(n);
                        });
                    }
                    else if (c.type !== 'paragraph' && c.type !== 'hardBreak' && c.type !== 'br') {
                        allContent.push(c);
                    }
                    if (c.type === 'pageBreak' || c.type === 'horizontalLineBlock' || c.type === 'horizontalRule') {
                        allContent.push(c);
                    }
                });
                const isHeader = cellNode.type === 'tableHeader';
                const items = processContentGrouped(allContent, false);
                const formatted = applyMarkupGrouping(items, false);
                let text = formatted.map(it => it.text).join('').replace(/\r?\n/g, ' ');
                if (isHeader) {
                    text = text.replace(/\*\*/g, '');
                }
                return text.trim() ? text : ' ';
            }
            // Check if first row is already an alignment indicator row (e.g. |==|==|)
            const firstRowCells = rows[0].content || [];
            const isFirstRowAlign = firstRowCells.length > 0 && firstRowCells.every((c) => {
                const t = extractCellFormattedText(c).trim();
                return t === '==' || t === '<=' || t === '=>' || t === '=&gt;' || t === '&lt;=';
            });
            const colAligns = [];
            for (let i = 0; i < colCount; i++) {
                let align = '<=';
                for (const row of rows) {
                    const cell = (row.content || [])[i];
                    if (cell) {
                        const cellAlign = cell.attrs?.textAlign || cell.content?.[0]?.attrs?.textAlign;
                        if (cellAlign === 'right') {
                            align = '=>';
                            break;
                        }
                        if (cellAlign === 'left') {
                            align = '<=';
                            break;
                        }
                        if (cellAlign === 'center') {
                            align = '==';
                            break;
                        }
                    }
                }
                colAligns.push(align);
            }
            const hasCustomAlign = colAligns.some(a => a === '==' || a === '=>');
            function createAlignRowParagraph() {
                const alignRowText = '|' + colAligns.join('|') + '|';
                return new Paragraph({
                    children: [
                        new TextRun({
                            text: alignRowText,
                            font: "Times New Roman",
                            size: pageSettings.fontSize * 2
                        })
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: {
                        line: 360,
                        lineRule: "auto",
                        after: 180,
                        before: 0
                    }
                });
            }
            function extractRowCondition(cells, row, tableNode) {
                let rawCond = row?.attrs?.rowCondition || tableNode?.attrs?.tableCondition;
                if (rawCond) {
                    const cleanKey = cleanConditionSyntax(rawCond).replace(/^Condition:\s*/i, '').replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
                    if (cleanKey) return cleanKey;
                }
                return null;
            }

            function extractCleanCellFormattedText(cellNode) {
                if (!cellNode) return ' ';
                const allContent = [];
                (cellNode.content || []).forEach((c) => {
                    if (c.content) {
                        c.content.forEach(n => {
                            if (n.type !== 'hardBreak' && n.type !== 'br') allContent.push(n);
                        });
                    }
                    else if (c.type !== 'paragraph' && c.type !== 'hardBreak' && c.type !== 'br') allContent.push(c);
                });
                const cleaned = allContent.map(item => {
                    if (!item.marks) return item;
                    return {
                        ...item,
                        marks: item.marks.filter(m => m.type !== 'conditionalBlock')
                    };
                });
                const isHeader = cellNode.type === 'tableHeader';
                const items = processContentGrouped(cleaned, false);
                const formatted = applyMarkupGrouping(items, false);
                let text = formatted.map(it => it.text).join('').replace(/\r?\n/g, ' ');
                if (isHeader) {
                    text = text.replace(/\*\*/g, '');
                }
                return text.trim() ? text : ' ';
            }

            if (!isFirstRowAlign) {
                childrenArray.push(createAlignRowParagraph());
            }

            // Output table rows as pipeline paragraphs
            rows.forEach((row, rIdx) => {
                const cells = row.content || [];
                const isAlign = cells.length > 0 && cells.every((c) => {
                    const t = extractCellFormattedText(c).trim();
                    return t === '==' || t === '<=' || t === '=>' || t === '=&gt;' || t === '&lt;=';
                });
                if (isAlign)
                    return;
                
                const isHeader = rIdx === 0 || cells.some(c => c?.type === 'tableHeader');
                const rowCond = !isHeader ? extractRowCondition(cells, row, node) : null;

                const rowCells = [];
                for (let i = 0; i < colCount; i++) {
                    const cell = cells[i];
                    if (!cell) {
                        rowCells.push(' ');
                    }
                    else {
                        let cellText = (rowCond || isHeader) ? extractCleanCellFormattedText(cell) : extractCellFormattedText(cell);
                        if (isHeader) {
                            cellText = cellText.replace(/\*\*/g, '');
                        }
                        rowCells.push(cellText.trim() ? cellText : ' ');
                    }
                }
                
                let formattedCond = null;
                if (rowCond) {
                    let c = cleanConditionSyntax(rowCond, row?.attrs?.rowConditionData);
                    c = c.replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();
                    if (c) {
                        if (!c.startsWith('<<') && !c.includes('<<')) {
                            const parts = c.split(/\s*==\s*/);
                            if (parts.length === 2) {
                                c = `<<${parts[0].replace(/<<|>>/g, '').trim()}>> == ${parts[1]}`;
                            } else {
                                c = `<<${c.replace(/<<|>>/g, '').trim()}>>`;
                            }
                        }
                        formattedCond = c;
                    }
                }
                const rowText = formattedCond 
                    ? '{|' + rowCells.join('|') + '| [' + formattedCond + '] }'
                    : '|' + rowCells.join('|') + '|';

                childrenArray.push(new Paragraph({
                    children: [
                        new TextRun({
                            text: rowText,
                            font: "Times New Roman",
                            size: pageSettings.fontSize * 2
                        })
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: {
                        line: 360,
                        lineRule: "auto",
                        after: 180,
                        before: 0
                    }
                }));
            });
            return;
        }
    }
    contentNodes.forEach((node) => processNode(node, docxChildren));

    // Post-process docxChildren to trim trailing blank paragraphs / page breaks
    while (docxChildren.length > 0) {
        const last = docxChildren[docxChildren.length - 1];
        const isPageBreak = last instanceof PageBreak;
        const isEmptyParagraph = last.children && last.children.length === 1 && (!last.children[0].text || last.children[0].text === ' ' || !last.children[0].text.trim());
        if (isPageBreak || isEmptyParagraph) {
            docxChildren.pop();
        } else {
            break;
        }
    }

    // Zero out bottom spacing on the very last paragraph to prevent Word pushing a blank page
    if (docxChildren.length > 0) {
        const lastPara = docxChildren[docxChildren.length - 1];
        if (lastPara && lastPara.options && lastPara.options.spacing) {
            lastPara.options.spacing.after = 0;
        }
    }

    const doc = new Document({
        styles: {
            default: {
                document: {
                    run: {
                        font: "Times New Roman",
                        size: pageSettings.fontSize * 2,
                        color: "000000"
                    },
                    paragraph: {
                        alignment: AlignmentType.JUSTIFIED,
                        spacing: {
                            line: 360,
                            lineRule: "auto",
                            after: 180,
                            before: 0
                        }
                    }
                }
            }
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: pageSettings.topMargin * 567,
                            bottom: pageSettings.bottomMargin * 567,
                            left: pageSettings.leftMargin * 567,
                            right: pageSettings.rightMargin * 567,
                            header: pageSettings.headerDistance * 567,
                            footer: pageSettings.footerDistance * 567
                        }
                    }
                },
                children: docxChildren
            },
        ]
    });
    return await Packer.toBlob(doc);
}
